import React from "react"
import { TableSize } from "../../type/mui"
import { HeaderData } from "../datatable/types"
import { Paper, Table, TableContainer } from "@mui/material"
import { Header } from "../datatable/component/Header"
import { Navigation } from "../datatable/component/Navigation"
import { Body } from "../datatable/component/Body"
import { Pagination } from "../datatable/component/Pagination"
import { useTable } from "../datatable/hook/useTable"
import { TableBodyData, TableBodyRow } from "../../store/common/reducer"
import { useTableStyles } from "../datatable/hook/useStyles"

type StaticDataTableProps<T extends Record<string, any>> = {
    title?: string
    size?: TableSize
    rows: T[]
    headerData: HeaderData[]
    renderBody: (row: T, idx: number) => React.ReactNode
    emptyText?: React.ReactNode
    pagination?: boolean
    limit?: number
}

export const StaticDataTable = <T extends Record<string, any>>({
    title, size = 'small', rows, headerData, renderBody,
    emptyText = 'No data', pagination = true, limit = 10,
}: StaticDataTableProps<T>) => {

    const classesTable = useTableStyles()
    const [params, setParams] = React.useState<Record<string, any>>({
        offset: 0,
        limit,
    })
    React.useEffect(() => {
        setParams(p => (p.limit === limit ? p : { ...p, limit }))
    }, [limit])

    const initialSortDirections = React.useMemo(
        () =>
            headerData.reduce((acc, h) => {
                acc[h.key] = 'DESC'
                return acc
            }, {} as Record<string, 'ASC' | 'DESC'>),
        [headerData],
    )
    const [sortDirections, setSortDirections] = React.useState(initialSortDirections)
    const [sortKey, setSortKey] = React.useState<string>(() => headerData.find(h => h.defaultSort)?.key ?? '')
    React.useEffect(() => {
        setSortDirections(initialSortDirections)
        setSortKey(headerData.find(h => h.defaultSort)?.key ?? '')
    }, [headerData, initialSortDirections])

    const handleSort = (key: string) => {
        const next = sortDirections[key] === 'ASC' ? 'DESC' : 'ASC'
        setSortKey(key)
        setSortDirections(s => ({ ...s, [key]: next }))
        setParams(p => ({ ...p, offset: 0 }))
        setPage(1)
    }

    const sortedRows = React.useMemo(() => {
        if (!sortKey) return rows
        const dir = sortDirections[sortKey] ?? 'DESC'
        const factor = dir === 'ASC' ? 1 : -1
        return [...rows].sort((a, b) => {
            const va = (a as any)?.[sortKey]
            const vb = (b as any)?.[sortKey]
            if (va == null && vb == null) return 0
            if (va == null) return -1 * factor
            if (vb == null) return 1 * factor
            if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * factor
            return String(va).localeCompare(String(vb)) * factor
        })
    }, [rows, sortKey, sortDirections])

    const [searchState, setSearchState] = React.useState('')
    const [lastSearch, setLastSearch] = React.useState('')
    const handleSearch = (value: string) => {
        setSearchState(value)
        setLastSearch(value)
        setParams(p => ({ ...p, offset: 0 }))
        setPage(1)
    }

    const filteredRows = React.useMemo(() => {
        const q = (searchState || '').trim().toLowerCase()
        if (!q) return sortedRows
        return sortedRows.filter(r =>
            Object.values(r ?? {}).some(v => String(v ?? '').toLowerCase().includes(q)),
        )
    }, [sortedRows, searchState])

    const fullBodyData: TableBodyData = React.useMemo(() => ({
        list: filteredRows as unknown as TableBodyRow[],
        total: filteredRows.length,
        count: filteredRows.length,
    }), [filteredRows])

    const {
        selectedRowIds,
        page,
        setPage,
        cellCount,
        pageCount,
        isPagesRowsSelected,
        handleSelect,
        handleSelectAll,
    } = useTable({
        headerData,
        bodyData: fullBodyData,
        params,
        selectable: false,
    })

    const slicedRows = React.useMemo(() => {
        const { offset = 0, limit: l = 10 } = params
        return filteredRows.slice(offset, offset + l)
    }, [filteredRows, params])

    const [collapsed, setCollapsed] = React.useState(false)

    const pageCountLocal = React.useMemo(() => {
        const l = params.limit ?? 10
        return Math.max(1, Math.ceil(filteredRows.length / l))
    }, [filteredRows.length, params.limit])

    const pageBodyData: TableBodyData = React.useMemo(() => ({
        list: slicedRows as unknown as TableBodyRow[],
        total: filteredRows.length,
        count: filteredRows.length,
    }), [slicedRows, filteredRows])

    return (
        <>
            <TableContainer component={Paper} variant="outlined" className={classesTable.card}>
                <Navigation
                    searchState={searchState}
                    setSearchState={setSearchState}
                    selectedRowIds={selectedRowIds}
                    selectNav={undefined}
                    title={title}
                    search
                    lastSearch={lastSearch}
                    onSearch={handleSearch}
                />
                <Table size={size}
                    sx={{
                        '& thead .MuiTableCell-root': { textAlign: 'center', whiteSpace: 'nowrap' },
                        '& tbody .MuiTableCell-root': { textAlign: 'center' },
                    }}
                >
                    <Header
                        headerData={headerData}
                        sortKey={sortKey}
                        sortDirections={sortDirections}
                        selectable={false}
                        isPagesRowsSelected={isPagesRowsSelected}
                        onSort={handleSort}
                        onSelectAll={handleSelectAll}
                    />

                    {!collapsed && (
                        <Body
                            bodyData={pageBodyData}
                            renderBody={renderBody}
                            cellCount={headerData.length}
                            selectable={false}
                            selectedRowIds={selectedRowIds}
                            onRowClick={undefined}
                            onSelect={handleSelect}
                            emptyText={emptyText}
                        />
                    )}
                </Table>
            </TableContainer>

            {pagination && (
                <Pagination
                    page={page}
                    setPage={setPage}
                    pageCount={pageCountLocal}
                    setParams={setParams}
                    limit={params.limit}
                />
            )}
        </>
    )
}
